import type { ComponentType, FC, ReactNode } from 'react';
import type React from 'react';
import { createContext, Suspense, useCallback, useContext, useMemo, useState } from 'react';

import { Loader, Modal } from '@/js/libs/ui';
import type { ModalProps } from '@/js/libs/ui/src/Feedback/Modal';

export interface ModalContext<TPayload extends Record<string, unknown>> {
  open: (payload: TPayload) => void;
  close: () => void;
  isOpen: boolean;
}

interface RenderPropContentProps<TPayload extends Record<string, unknown>> {
  component?: undefined;
  render: (payload: TPayload) => ReactNode;
}

interface ComponentContentProps<TPayload extends Record<string, unknown>> {
  component: ComponentType<{ payload: TPayload }>;
  render?: undefined;
}

type ContentProps<TPayload extends Record<string, unknown>> = RenderPropContentProps<TPayload> | ComponentContentProps<TPayload>;

type ProviderProps<TPayload extends Record<string, unknown>> = Partial<ModalProps> & ContentProps<TPayload>;

const createModalContext = <TPayload extends Record<string, unknown>>(
  name: string,
): [Provider: React.FC<ProviderProps<TPayload>>, useModalContext: <TActualPayload extends TPayload>() => ModalContext<TActualPayload>] => {
  const Context = createContext<ModalContext<TPayload> | null>(null);

  const contextName = `${name}Context`;
  const providerName = `${name}Provider`;

  Context.displayName = contextName;

  const ModalProvider: FC<ProviderProps<TPayload>> = (props) => {
    const { children, component: Component, render, ...modalProps } = props;

    const [payload, setPayload] = useState<TPayload | null>(null);

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const handleOpen = useCallback((value: TPayload) => {
      setPayload(value);
    }, []);

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const handleClose = useCallback(() => setPayload(null), []);

    const isOpen = !!payload;

    const contextValue = useMemo(
      () => ({
        open: handleOpen,
        close: handleClose,
        isOpen,
      }),
      [handleClose, handleOpen, isOpen],
    );

    const modalContent = useMemo<ReactNode>(() => {
      if (!payload) {
        return null;
      }

      if (render) {
        return render(payload);
      }

      return (
        <Suspense fallback={<Loader />}>
          <Component payload={payload} />
        </Suspense>
      );
    }, [Component, payload, render]);

    return (
      <Context.Provider value={contextValue}>
        {children}
        <Modal {...modalProps} isOpen={isOpen} onRequestClose={handleClose}>
          {modalContent}
        </Modal>
      </Context.Provider>
    );
  };

  ModalProvider.displayName = providerName;

  const useModalContext = (): ModalContext<TPayload> => {
    const value = useContext(Context);

    if (value === null) {
      throw new Error(`\`use${contextName}\` is used outside of \`${providerName}\``);
    }

    return value;
  };

  return [ModalProvider, useModalContext];
};

export default createModalContext;
